import React from 'react'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { ExplanationTemplate } from 'components/screen-templates/ExplanationTemplate'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { RatingsRow } from '../core/social-proof/RatingsRow'
import { DeferredGif } from '../core/ziggie/DeferredGif'
import { Quizzes } from '../../redux/quiz/types'
import { GoalCopy, resolveGoalTokens } from '../../libs/goals/goalCopy'
import { GifName, resolveTopImage } from '../../data/gifs'

export type ContentScreenProps = {
  title: string
  description?: string
  footerBody?: string
  externalLinks?: { url: string; text: string }[]
  topImage?: string
  gif?: GifName
  topImageAlt?: string
  topImageAspectRatio?: string
  subtitleAboveTitle?: boolean
  titleAboveImage?: boolean
  imageObjectFit?: 'cover' | 'contain'
  centerText?: boolean
  tightTop?: boolean
  tightFooter?: boolean
  tightImage?: boolean
  squareImage?: boolean
  regularFooter?: boolean
  largeTitle?: boolean
  // e12: the top image runs edge to edge on phones.
  topImageBleed?: boolean
  // e12: the app rating and monthly users row under the title.
  showRatingsRow?: boolean
  // e12: an animated GIF over the top image, positioned in % of the image's box.
  // The still shows at once; the GIF loads after the page.
  topImageGif?: { gif: GifName; still: string; left: string; top: string; width: string }
  descriptionBelowTopImage?: string
  topImageLink?: string
  buttonText?: string
  hideContinueButton?: boolean
  attribution?: string
  attributionImage?: string
  showHomePageLink?: boolean
  // Tokens whose copy depends on the goals answer, e.g. { goalEnding: {...} }
  // lets the description use {goalEnding}.
  goalTokens?: Record<string, GoalCopy>
}

type Props = ContentScreenProps & {
  onNextClick: onNextClickType
  onBackClick: onBackClickCallback
  quiz: Quizzes
}

export const ContentScreen: React.FC<Props> = (props) => {
  const image = resolveTopImage(props.gif, props)
  const goalTokens = resolveGoalTokens('goals' in props.quiz ? props.quiz.goals : undefined, props.goalTokens)
  return (
    <ExplanationTemplate
      image={image.topImage}
      imageAlt={image.topImageAlt}
      imageAspectRatio={image.topImageAspectRatio}
      subtitleAboveTitle={props.subtitleAboveTitle}
      titleAboveImage={props.titleAboveImage}
      imageObjectFit={props.imageObjectFit}
      centerText={props.centerText}
      tightTop={props.tightTop}
      tightFooter={props.tightFooter}
      tightImage={props.tightImage}
      squareImage={props.squareImage}
      regularFooter={props.regularFooter}
      largeTitle={props.largeTitle}
      imageBleed={props.topImageBleed}
      belowTitle={props.showRatingsRow ? <RatingsRow /> : undefined}
      imageOverlay={
        props.topImageGif && (
          <DeferredGif
            gif={props.topImageGif.gif}
            still={props.topImageGif.still}
            style={{
              position: 'absolute',
              left: props.topImageGif.left,
              top: props.topImageGif.top,
              width: props.topImageGif.width,
              maxWidth: 'none',
            }}
          />
        )
      }
      descriptionBelowImage={
        props.descriptionBelowTopImage ? (
          <ContextAwareText text={props.descriptionBelowTopImage} quiz={props.quiz} />
        ) : undefined
      }
      title={props.title}
      subTitle={<ContextAwareText text={props.description} quiz={props.quiz} additionalTokens={goalTokens} />}
      footerBody={props.footerBody ? <ContextAwareText text={props.footerBody} /> : undefined}
      attribution={props.attribution}
      attributionImage={props.attributionImage}
      cta={props.buttonText ?? 'Continue'}
      onNextClick={props.onNextClick}
      onBackClick={props.onBackClick}
      showHomepageLink={props.showHomePageLink ?? false}
      hideContinueButton={props.hideContinueButton ?? false}
      externalLinks={props.externalLinks}
    />
  )
}
